import { TextField, Tooltip, IconButton, LinearProgress, Box } from "@mui/material";
import {Refresh, Lock, Check, Cancel, Info} from "@mui/icons-material";
import { useOktaAuth } from "@okta/okta-react";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AreaContext } from "../context/AreaContext";
import { convertTextToBytes } from "../utils/ByteConverter";

const POLL_INTERVAL_MS = 30000;

const _QuotaEntry = ({ type, id, owner, employeeType }) => {
  const { authState } = useOktaAuth();
  const { area, setArea } = useContext(AreaContext);

  const [quota, setQuota] = useState("");
  const [siteUrl, setSiteUrl] = useState(null);
  const [newQuota, setNewQuota] = useState("");
  const [locked, setLocked] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const hasFetchedRef = useRef(false);

  const fetchQuota = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const endpoint = type === "Mailbox" ? "quota/exchange" : "quota/sharepoint";

      const res = await fetch(
        `${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}${endpoint}?id=${id}`,
        {
          headers: {
            Authorization: authState.accessToken.accessToken,
            Accept: "application/json",
          },
        }
      );

      if (!res.ok) {
        const errObj = await res.json().catch(() => ({}));
        throw new Error(errObj?.error?.message || "Failed to fetch quota");
      }

      const data = await res.json();

      setArea(prev => ({ ...prev, quotas: { ...prev.quotas, [id]: {quota: data.quota, siteUrl: data.url} } }));
      if(data.url){
        setSiteUrl(data.url);
      }
      setQuota(data.quota);
      setNewQuota(data.quota);
    } catch (err) {
      console.error("Quota fetch error:", err);
      setError(err.message || "Error fetching quota");
    } finally {
      setIsLoading(false);
    }
  }, [authState, id, type, setArea]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    if (area.quotas?.[id]) {
      setQuota(area.quotas[id].quota);
      if(area.quotas[id].siteUrl){
        setSiteUrl(area.quotas[id].siteUrl);
      }
      setNewQuota(area.quotas[id].quota);
    } else {
      fetchQuota();
    }
  }, [area.quotas, fetchQuota, id]);

  const handleRefresh = () => fetchQuota();
  const handleUnlock = () => setLocked(false);
  const handleCancel = () => {
    setNewQuota(quota);
    setLocked(true);
  };
  const handleChange = (e) => setNewQuota(e.target.value);

  const handleConfirm = async () => {
    if (newQuota === quota) {
      setLocked(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {

      let bytes = convertTextToBytes(newQuota);

      if( bytes < 1073741824 && type !== "Mailbox"){
        alert("OneDrive, Teams, and SharePoint do not support a quota of size less than 1 GB.  Please change your quota");
        setIsLoading(false);
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}quota/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authState.accessToken.accessToken,
        },
        body: JSON.stringify({
          id,
          resource_type: type,
          employee_type: employeeType,
          owner,
          quota: convertTextToBytes(newQuota),
        }),
      });

      if (!res.ok) {
        const errObj = await res.json().catch(() => ({}));
        throw new Error(errObj?.error?.message || "Failed to submit quota update");
      }

      const { jobId } = await res.json();

      let completed = false;
      while (!completed) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

        const statusRes = await fetch(
          `${process.env.REACT_APP_QM_AWS_API_GATEWAY_ENDPOINT}job?jobId=${jobId}`,
          {
            headers: {
              Authorization: authState.accessToken.accessToken,
              Accept: "application/json",
            },
          }
        );

        if (!statusRes.ok) continue;

        const jobData = await statusRes.json();
        if (jobData.completed)
        {
          completed = true;
          if(jobData.failedRecords.length !== 0){
            setError(jobData.failedRecords[0].exception);
          }
        }
      }

      setQuota(newQuota);
      setLocked(true);

    } catch (err) {
      console.error("Quota update error:", err);
      setError(err.message || "Error updating quota");
    } finally {
      setIsLoading(false);
    }
  };

  const renderButtons = () => {
    if (locked) {
      return (
        <>
          <Tooltip title="Refresh quota">
            <IconButton disabled={isLoading} onClick={handleRefresh}>
              <Refresh />
            </IconButton>
          </Tooltip>

          <Tooltip title="Unlock row">
            <IconButton disabled={isLoading} onClick={handleUnlock}>
              <Lock />
            </IconButton>
          </Tooltip>
          {siteUrl !== null &&
              <Tooltip title="Click to copy Site URL">
                <IconButton
                    onClick={() => navigator.clipboard.writeText(siteUrl)}
                >
                  <Info />
                </IconButton>
              </Tooltip>
          }
        </>
      );
    }

    return (
      <>
        <Tooltip title="Confirm changes">
          <IconButton disabled={isLoading} onClick={handleConfirm}>
            <Check />
          </IconButton>
        </Tooltip>

        <Tooltip title="Discard changes">
          <IconButton disabled={isLoading} onClick={handleCancel}>
            <Cancel />
          </IconButton>
        </Tooltip>
        {siteUrl !== null &&
            <Tooltip title="Click to copy Site URL">
              <IconButton
                  onClick={() => navigator.clipboard.writeText(siteUrl)}
              >
                <Info />
              </IconButton>
            </Tooltip>
        }
      </>
    );
  };

  if (isLoading)
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 56,
          width: "100%",
        }}
      >
        <LinearProgress
          sx={{
            width: "80%",
            backgroundColor: "#e0e0e0",
            "& .MuiLinearProgress-bar": { backgroundColor: "#A60F2D" },
          }}
        />
      </Box>
    );

  if(error){
    return (
        <>
          <Tooltip title={error.toString()} placement="top">
            <TextField value={"ERROR"} onChange={handleChange} disabled={locked} size="small"
                       onKeyDown={(event) => {
                         if(event.key === ' '){
                           event.stopPropagation();
                         }
                       }}/>
          </Tooltip>
          {renderButtons()}
        </>
    );
  }

  return (
      <>
        <TextField value={newQuota} onChange={handleChange} disabled={locked} size="small"
                   onKeyDown={(event) => {
                     if(event.key === ' '){
                       event.stopPropagation();
                     }
                   }}/>
        {renderButtons()}
      </>
  );
};

export const QuotaEntry = React.memo(_QuotaEntry);
